import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { SvgGroupPaperclip } from '../../../../../gen-src/icons';
import { DatabaseOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createServerOrPathString,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  DatabaseMediumIcon,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  getDatabaseIconTooltip,
  GraphQLError,
  GraphLabel,
  GridSortProps,
  messageForConnectionType,
  reportGraphQLErrors,
  SiteUrlsContext,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
  LabelContent,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import {
  createObfuscatedLinkWithIconCellFormatter,
  createObfuscatedTextCellFormatter,
  PermissionsRequiredCell,
} from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createProjectLocationLinkCellFormatter } from '../datagrid/cellFormatters/ProjectLocationCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import {
  GlobalAssetGridDC3ColumnFactoryParams,
} from '../sorting/makeColumnFactoryParams';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { CellFormatter } from './types/SharedDisplayGridTypes';

const shouldBeObfuscated: (rowData: DatabasesDisplayGridRowData) => boolean = ({
  name,
}) => name === null;

type DatabasesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<DatabasesValidSortFields>,
  DatabasesDisplayGridRowData,
  DatabasesValidSortFields
>;
interface IconColumnDatabaseParams {
  rowData: DatabasesDisplayGridRowData;
}

function iconColumn(params: IconColumnDatabaseParams): JSX.Element {
  const paramsOverride: IconColumnDatabaseParams = {
    ...params,
    rowData: {
      ...params.rowData,
      isCertified: false,
    },
  };
  return <DatabaseMediumIcon database={paramsOverride.rowData} />;
}

function databaseGroupIcon(
  rowData: DatabasesDisplayGridRowData
): JSX.Element | null {
  if (!rowData.isGrouped) {
    return null;
  }

  return (
    <div
      role='img'
      aria-label={Messages.GroupedAsset()}
      title={Messages.GroupedAsset()}
    >
      <SvgGroupPaperclip />
    </div>
  );
}

function databaseLinkTitle(
  rowData: DatabasesDisplayGridRowData
): string | null {
  return rowData.name;
}

export function columns(siteUrls: SiteUrls): ReadonlyArray<DatabasesColumnFactory> {
  const columns: ReadonlyArray<DatabasesColumnFactory> = [
    (sortParams) => ({
      name: 'type',
      dataKey: DatabaseOrderField.IS_CERTIFIED,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        (rowData) =>
          iconColumn({
            rowData: rowData,
          }),
        (rowData) => (rowData.isCertified ? '' : getDatabaseIconTooltip(rowData))
      ),
      sortable: true,
      resizable: false,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'CERTIFICATION'),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (params) => ({
      name: 'name',
      dataKey: DatabaseOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(params),
      cellFormatter: createObfuscatedLinkWithIconCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name, // getTextFromRowData
        (urls, { id }) => urls.catalogDatabasePath(id), // getPathFromRowData
        false, // noPadding,
        true, // disableTabIndex
        PermissionsRequiredCell, // ObfuscatedComponent
        databaseGroupIcon,
        databaseLinkTitle,
        (rowData) => rowData.id === '' // graphQLId
      ),
      growToFit: true,
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'virtualConnections',
      dataKey: DatabaseOrderField.DOWNSTREAM_VIRTUAL_CONNECTIONS_COUNT,
      headerText: Messages.GridHeaderVirtualConnection(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamVirtualConnectionsConnection }) =>
          downstreamVirtualConnectionsConnection
            ? Formatters.formatCount(
              downstreamVirtualConnectionsConnection.totalCount
            )
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: DatabaseOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamWorkbooksConnection }) =>
          downstreamWorkbooksConnection
            ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: DatabaseOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamDatasourcesConnection }) =>
          downstreamDatasourcesConnection
            ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'serverOrPath',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderServerOrPath(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedTextCellFormatter<
        DatabasesDisplayGridRowData
      >(
        shouldBeObfuscated,
        createServerOrPathString,
        undefined, // placeholder
        (rowData) => rowData.id === ''
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'connectionType',
      dataKey: DatabaseOrderField.CONNECTION_TYPE,
      headerText: Messages.GridHeaderConnectionType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedTextCellFormatter(
        shouldBeObfuscated,
        (rowData) => messageForConnectionType(rowData.connectionType),
        undefined, // placeholder
        (rowData) => rowData.id === ''
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'contact',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderContact(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(
        ({ contact }) => contact,
        true,
        (rowData) => rowData.id === ''
      ),
      resizable: true,
      sortable: false,
    }),
  ];

  return columns.filter(Boolean) as ReadonlyArray<DatabasesColumnFactory>;
}

/**
 * @deprecated These cell formatters are already in use in the columns above. These defs below will be removed once we have
 * the columns used properly in vpc.
 */
const cellFormattersForCrass: Record<string, CellFormatter> = {
  typeForCrass: {
    type: 'database',
    cellFormatter: createIconCellFormatter(
      (rowData) =>
        iconColumn({
          rowData: rowData,
        }),
      (rowData) => (rowData.isCertified ? '' : getDatabaseIconTooltip(rowData))
    ),
  },
  name: {
    type: 'database',
    cellFormatter: createObfuscatedLinkWithIconCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name, // getTextFromRowData
      (urls, { id }) => urls.catalogDatabasePath(id), // getPathFromRowData
      false, // noPadding,
      true, // disableTabIndex
      PermissionsRequiredCell, // ObfuscatedComponent
      databaseGroupIcon,
      databaseLinkTitle,
      (rowData) => rowData.id === ''
    ),
  },
  downstreamWorkbookCount: {
    type: 'database',
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
  },
  downstreamDataSourcesCount: {
    type: 'database',
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
  },
  serverOrPath: {
    type: 'database',
    cellFormatter: createObfuscatedTextCellFormatter<
      DatabasesDisplayGridRowData
    >(
      shouldBeObfuscated,
      createServerOrPathString,
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
  },
  connectionTypeCrass: {
    type: 'database',
    cellFormatter: createObfuscatedTextCellFormatter(
      shouldBeObfuscated,
      (rowData) => messageForConnectionType(rowData.connectionType),
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
  },
  contact: {
    type: 'database',
    cellFormatter: createUserNameLinkCellFormatter(
      ({ contact }) => contact,
      true,
      (rowData) => rowData.id === ''
    ),
  },
  databaseProjectLocation: {
    type: 'database',
    cellFormatter: createProjectLocationLinkCellFormatter(
      (rowData) => { return rowData.containerId === undefined ? null : rowData.containerId; },
      (rowData) => { return rowData.containerName === undefined ? null : rowData.containerName; },
      true,
      (rowData) => { return rowData.isEmbedded === undefined ? null : rowData.isEmbedded; },
      (rowData) => rowData.id === ''
    ),
  },
};


export const databasesWithCrassCellFormatters = cellFormattersForCrass;

export type DatabasesDisplayGridProps = UsualDisplayGridProps<
  DatabasesDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<DatabasesValidSortFields>;

export function DatabasesDisplayGrid(
  props: DatabasesDisplayGridProps
): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls)
    .map((columnFactory) => columnFactory(props))
    .filter(column => {
      if (column.name === 'virtualConnections' && !publishedConnectionsLineageEnabled) {
        return false;
      }
      return true;
    });
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

function getLabelContentFromRowData(rowData: DatabasesDisplayGridRowData, urls: SiteUrls) {
  return {
    contentId: rowData.id,
    contentLuid: rowData.luid,
    contentType: rowData.contentType,
    contentName: rowData.name ?? undefined,
    contentUrl: urls.catalogDatabaseUrl(rowData.id),
    subjectArea: rowData.contentType
  };
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<DatabasesDisplayGridRowData, DatabasesValidSortFields> {

  function getPropsFromRowData(rowData: DatabasesDisplayGridRowData): UseRenderLabelsParams {
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    return getLabelParamsForGrids({ category, labels, urls, source: 'databases', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<DatabasesDisplayGridRowData, DatabasesValidSortFields>;
}
